import { useQuery } from "@tanstack/react-query";
import { api } from "../../api";

type Report = {
  byStatus: Record<"open" | "waiting" | "closed", number>;
  byOwner: { userId: string; name: string; open: number; worksheetsReady: number }[];
  overdueFollowups: number;
  emailsSent: number;
  programsByStatus: Record<"recommended" | "referred" | "applied" | "approved" | "denied" | "withdrawn", number>;
};

const PROGRAMS: { key: keyof Report["programsByStatus"]; label: string }[] = [
  { key: "recommended", label: "Recommended" },
  { key: "referred", label: "Referred" },
  // These four are what the customer told us; the portal does not verify them.
  { key: "applied", label: "Applied (customer reported)" },
  { key: "approved", label: "Approved (customer reported)" },
  { key: "denied", label: "Denied (customer reported)" },
  { key: "withdrawn", label: "Withdrawn (customer reported)" },
];

function Tile({ label, value }: { label: string; value: number }) {
  return <div className="card"><div className="text-xs text-slate-500">{label}</div><div className="text-2xl font-semibold">{value}</div></div>;
}

export function Reports() {
  const q = useQuery({ queryKey: ["reports", "cases"], queryFn: () => api<Report>("/api/reports/cases") });
  const r = q.data;
  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <h1 className="text-xl font-semibold">Case reports</h1>
      {q.error && <p role="alert" className="text-sm text-red-600">{(q.error as Error).message}</p>}
      {r && (
        <>
          <section className="flex flex-col gap-2">
            <h2 className="text-sm font-semibold">Cases</h2>
            <div className="grid gap-3 sm:grid-cols-5">
              <Tile label="Open" value={r.byStatus.open} />
              <Tile label="Waiting" value={r.byStatus.waiting} />
              <Tile label="Closed" value={r.byStatus.closed} />
              <Tile label="Overdue follow-ups" value={r.overdueFollowups} />
              <Tile label="Emails sent (30 days)" value={r.emailsSent} />
            </div>
          </section>
          <section className="flex flex-col gap-2">
            <h2 className="text-sm font-semibold">Programs on cases</h2>
            <div className="grid gap-3 sm:grid-cols-3">{PROGRAMS.map((p) => <Tile key={p.key} label={p.label} value={r.programsByStatus[p.key]} />)}</div>
          </section>
          <section className="flex flex-col gap-2">
            <h2 className="text-sm font-semibold">Open and waiting cases by owner</h2>
            <table className="card w-full text-left text-sm">
              <thead className="text-xs uppercase text-slate-500"><tr><th className="py-2">Owner</th><th>Cases</th><th>Worksheets ready</th></tr></thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {r.byOwner.map((o) => <tr key={o.userId}><td className="py-2">{o.name}</td><td>{o.open}</td><td>{o.worksheetsReady}</td></tr>)}
                {r.byOwner.length === 0 && <tr><td className="py-2 text-slate-500" colSpan={3}>No open cases.</td></tr>}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
}
